import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Languages } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import {
  PUBLIC_SURVEY_LANG_KEY,
  PUBLIC_SURVEY_LANGUAGES,
  normalizePublicSurveyLanguage,
  type PublicSurveyLanguage,
  publicSurveyI18n,
} from './i18n';

const LANGUAGE_SHORT: Record<PublicSurveyLanguage, string> = {
  'pt-BR': 'BR',
  'pt-PT': 'PT',
  en: 'EN',
  es: 'ES',
};

export function PublicSurveyLanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const current = normalizePublicSurveyLanguage(i18n.language);

  const handleChange = (value: string) => {
    const lng = value as PublicSurveyLanguage;
    void publicSurveyI18n.changeLanguage(lng);
    localStorage.setItem(PUBLIC_SURVEY_LANG_KEY, lng);

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('lang', lng);
        return next;
      },
      { replace: true },
    );
  };

  return (
    <div className="flex items-center gap-1.5">
      <span className="hidden text-xs text-muted-foreground sm:inline">{t('topBar.language')}</span>
      <Select value={current} onValueChange={handleChange}>
        <SelectTrigger
          className="h-9 w-auto min-w-[4.5rem] gap-1.5 border-border/60 bg-background px-2 shadow-sm sm:min-w-[9rem] sm:px-3"
          aria-label={t('topBar.language')}
        >
          <Languages className="size-4 shrink-0 opacity-70" />
          <SelectValue>
            <span className="sm:hidden">{LANGUAGE_SHORT[current] ?? current}</span>
            <span className="hidden sm:inline">{t(`languages.${current}`)}</span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent align="end">
          {PUBLIC_SURVEY_LANGUAGES.map((lng) => (
            <SelectItem key={lng} value={lng}>
              {t(`languages.${lng}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
